import { isUuid } from '@leanin/shared';
import type { GroupKind } from '@/lib/group-url';

// Group destination carried across onboarding for join-link signups.
// Written at /auth/finishing when the attribution claim reports the signup
// arrived through a group join link; read once by the onboarding completion
// step to land the new member on the advertised group instead of the
// generic /feed. Cleared on EVERY onboarding finish (not just when read on
// the happy path) so a stale value can't leak into a later signup in the
// same tab — the pendingInviteToken equivalent of that leak already bit us.
const POST_ONBOARDING_GROUP_KEY = 'onboarding.postOnboardingGroup';

export interface PostOnboardingGroup {
  readonly kind: GroupKind;
  readonly id: string;
}

const GROUP_KINDS: ReadonlyArray<GroupKind> = ['circle', 'network', 'space'];

export function writePostOnboardingGroup(group: PostOnboardingGroup): void {
  if (typeof window === 'undefined') return;
  window.sessionStorage.setItem(POST_ONBOARDING_GROUP_KEY, JSON.stringify(group));
}

export function readPostOnboardingGroup(): PostOnboardingGroup | null {
  if (typeof window === 'undefined') return null;
  const raw = window.sessionStorage.getItem(POST_ONBOARDING_GROUP_KEY);
  if (raw === null) return null;
  try {
    const parsed: unknown = JSON.parse(raw);
    const candidate = parsed as { kind?: unknown; id?: unknown };
    if (
      typeof candidate.kind === 'string' &&
      (GROUP_KINDS as ReadonlyArray<string>).includes(candidate.kind) &&
      typeof candidate.id === 'string' &&
      isUuid(candidate.id)
    ) {
      return { kind: candidate.kind as GroupKind, id: candidate.id };
    }
    return null;
  } catch {
    return null;
  }
}

export function clearPostOnboardingGroup(): void {
  if (typeof window === 'undefined') return;
  window.sessionStorage.removeItem(POST_ONBOARDING_GROUP_KEY);
}
